import type {
  Sort,
  SortItem,
  StructuredExpression,
  StructuredFilter,
} from '../../../resources/filterTypes'
import { parseRelativeDateFilterValue } from '../../../resources/relativeDateFilter'
import type { Query } from '../features/queries/resources/types'

const OPERATOR_MAP: Record<string, string> = {
  eq: 'eq',
  ne: 'neq',
  gt: 'gt',
  gte: 'gte',
  lt: 'lt',
  lte: 'lte',
}

const parseFilterValue = (value: string): string | number | boolean => {
  if (parseRelativeDateFilterValue(value)) return value
  if (value === 'true') return true
  if (value === 'false') return false
  const num = Number(value)
  if (!Number.isNaN(num) && value.trim() !== '') return num
  return value
}

const uniqueClauses = (clauses: StructuredFilter[]): StructuredFilter[] => {
  const seen = new Set<string>()
  const unique: StructuredFilter[] = []
  for (const clause of clauses) {
    const key = JSON.stringify(clause)
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(clause)
  }
  return unique
}

/**
 * Convert legacy Page.baseQuery filters to StructuredExpression.
 */
export const convertBaseQueryToStructured = (
  baseQuery: Query | null | undefined,
): StructuredExpression | undefined => {
  if (!baseQuery?.filters?.length) {
    return undefined
  }

  const filters = uniqueClauses(
    baseQuery.filters.map((filter) => {
      const op = OPERATOR_MAP[filter.operator] ?? filter.operator
      return {
        [filter.field]: {
          [op]: parseFilterValue(filter.value),
        },
      }
    }),
  )

  if (filters.length === 1) {
    return filters[0]
  }

  return { AND: filters }
}

export const convertBaseQuerySort = (
  baseQuery: Query | null | undefined,
): Sort | undefined => {
  if (!baseQuery?.sort?.field) {
    return undefined
  }

  return {
    key: baseQuery.sort.field,
    direction: baseQuery.sort.direction ?? 'desc',
  }
}

const isStructuredFilter = (value: unknown): value is StructuredFilter =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !('AND' in value) &&
  !('OR' in value) &&
  !('NOT' in value)

const isEmptyExpression = (expr: StructuredExpression | undefined): boolean => {
  if (!expr) return true
  if (isStructuredFilter(expr)) {
    return Object.keys(expr).length === 0
  }
  const obj = expr as {
    AND?: StructuredFilter[]
    OR?: StructuredFilter[]
    NOT?: unknown
  }
  if (obj.AND?.length) return false
  if (obj.OR?.length) return false
  if (obj.NOT) return false
  return true
}

const expressionsEqual = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right)

const andClauses = (
  expr: StructuredExpression,
): StructuredFilter[] | undefined => {
  if (isStructuredFilter(expr)) return undefined
  const clauses = (expr as { AND?: StructuredFilter[] }).AND
  return clauses?.length ? clauses : undefined
}

/** Collapse duplicate AND siblings (`AND [base, base]` → `base`). */
export const normalizeStructuredExpression = (
  expr: StructuredExpression | undefined,
): StructuredExpression | undefined => {
  if (!expr || isEmptyExpression(expr)) return expr
  const clauses = andClauses(expr)
  if (!clauses) return expr
  const unique = uniqueClauses(clauses)
  if (unique.length === 0) return undefined
  if (unique.length === 1) return unique[0]
  return unique.length === clauses.length ? expr : { AND: unique }
}

/** True when `expr` is the base view, or an AND that already contains it. */
export const expressionIncludes = (
  expr: StructuredExpression,
  included: StructuredExpression,
): boolean => {
  if (expressionsEqual(expr, included)) return true

  const exprAnd = andClauses(expr)
  if (exprAnd?.some((part) => expressionsEqual(part, included))) {
    return true
  }

  const includedAnd = andClauses(included)
  if (exprAnd && includedAnd) {
    return includedAnd.every((basePart) =>
      exprAnd.some((part) => expressionsEqual(part, basePart)),
    )
  }

  return false
}

/**
 * Merge persisted baseQuery filters with ephemeral URL/tRPC filters.
 * Ephemeral filters are AND-ed on top of the base view definition.
 */
export const mergePageFilters = (
  baseQuery: Query | null | undefined,
  ephemeralFilters?: unknown,
): StructuredExpression | undefined => {
  const baseExpr = convertBaseQueryToStructured(baseQuery)
  const hasBase = !isEmptyExpression(baseExpr)
  const hasEphemeral =
    ephemeralFilters &&
    typeof ephemeralFilters === 'object' &&
    !isEmptyExpression(ephemeralFilters as StructuredExpression)

  if (!hasBase && !hasEphemeral) {
    return undefined
  }

  if (hasBase && !hasEphemeral) {
    return normalizeStructuredExpression(baseExpr)
  }

  if (!hasBase && hasEphemeral) {
    return normalizeStructuredExpression(
      ephemeralFilters as StructuredExpression,
    )
  }

  const ephemeralExpr = ephemeralFilters as StructuredExpression
  if (baseExpr && expressionIncludes(ephemeralExpr, baseExpr)) {
    return normalizeStructuredExpression(ephemeralExpr)
  }

  return normalizeStructuredExpression({
    AND: [
      baseExpr as StructuredFilter,
      ephemeralExpr as StructuredFilter,
    ],
  })
}

const normalizeSort = (
  sort:
    | SortItem
    | SortItem[]
    | { key: string; direction?: 'asc' | 'desc' }
    | Array<{ key: string; direction?: 'asc' | 'desc' }>
    | undefined,
): Sort | undefined => {
  if (!sort) return undefined
  if (Array.isArray(sort)) {
    return sort.length > 0 ? sort : undefined
  }
  return sort
}

/**
 * Ephemeral sort wins; fall back to baseQuery.sort when no ephemeral sort.
 */
export const mergePageSort = (
  baseQuery: Query | null | undefined,
  ephemeralSort?: unknown,
): Sort | undefined => {
  const ephemeral = normalizeSort(
    ephemeralSort as
      | SortItem
      | SortItem[]
      | { key: string; direction?: 'asc' | 'desc' }
      | undefined,
  )
  if (ephemeral) {
    return ephemeral
  }
  return convertBaseQuerySort(baseQuery)
}

const sortKeyUsesModelField = (key: string, modelFieldHandles: Set<string>) => {
  let topLevel = key.split('.')[0]?.trim()
  if (!topLevel) return false
  // UI / persisted views may use camelCase instance timestamps; model fields are snake_case.
  if (topLevel === 'createdAt') topLevel = 'created_at'
  else if (topLevel === 'updatedAt') topLevel = 'updated_at'
  return modelFieldHandles.has(topLevel)
}

/**
 * Drop sort keys that do not exist on the page model. Invalid persisted sorts
 * fall back to instance createdAt ordering in getInstances.
 */
export const sanitizeSortForModel = (
  sort: Sort | undefined,
  modelFieldHandles: Iterable<string>,
): Sort | undefined => {
  if (!sort) {
    return undefined
  }

  const handles = new Set(modelFieldHandles)
  const specs = Array.isArray(sort) ? sort : [sort]
  const valid = specs.filter((spec) => sortKeyUsesModelField(spec.key, handles))

  if (valid.length === 0) {
    return undefined
  }

  return valid.length === 1 ? valid[0] : valid
}
